import React, {useEffect, useMemo, useState} from "react";

import {RangeSlider} from "components/ClientHome/CalculatorComponents";
import {
  calculateLumpsumPlan,
  DEFAULT_INPUT,
  INPUT_LIMITS,
  snapToStep
} from "./CalculatorLogic";
import {
  DonutChartSection,
  MobileTabToggle,
  SummaryCards,
  ViewToggle,
  YearlyTable
} from "./CalculatorComponents";
import {colors} from "nxtconstants";
import {
  CalculatorSection,
  CalculatorWrapper,
  ControlPanel,
  PageTitle,
  ResultsPanel
} from "./CalculatorStyle";

const GoalPlanningLumpsumCalculator = () => {
  const [targetCorpus, setTargetCorpus] = useState(DEFAULT_INPUT.targetCorpus);
  const [targetPeriod, setTargetPeriod] = useState(DEFAULT_INPUT.targetPeriod);
  const [returnRate, setReturnRate] = useState(DEFAULT_INPUT.returnRate);
  const [activeView, setActiveView] = useState("graph");
  const [mobileTab, setMobileTab] = useState("input");
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setChartKey((previous) => previous + 1);
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const result = useMemo(
    () =>
      calculateLumpsumPlan({
        targetCorpus,
        targetPeriod,
        returnRate
      }),
    [targetCorpus, targetPeriod, returnRate]
  );

  const sliderProps = {
    accentColor: colors.primary,
    showRangeLabels: false
  };

  return (
    <CalculatorSection>
      <PageTitle>Goal Planning Lumpsum Calculator</PageTitle>

      <MobileTabToggle activeTab={mobileTab} onChange={setMobileTab} />

      <CalculatorWrapper>
        <ControlPanel $hidden={mobileTab === "result"}>
          <RangeSlider
            label="Target Corpus"
            value={targetCorpus}
            onChange={(value) =>
              setTargetCorpus(snapToStep(value, INPUT_LIMITS.targetCorpus.step))
            }
            min={INPUT_LIMITS.targetCorpus.min}
            max={INPUT_LIMITS.targetCorpus.max}
            step={INPUT_LIMITS.targetCorpus.step}
            prefix="₹"
            {...sliderProps}
          />

          <RangeSlider
            label="Time to Reach Target"
            value={targetPeriod}
            onChange={setTargetPeriod}
            min={INPUT_LIMITS.targetPeriod.min}
            max={INPUT_LIMITS.targetPeriod.max}
            step={INPUT_LIMITS.targetPeriod.step}
            suffix=" Years"
            {...sliderProps}
          />

          <RangeSlider
            label="Expected Return Rate"
            value={returnRate}
            onChange={setReturnRate}
            min={INPUT_LIMITS.returnRate.min}
            max={INPUT_LIMITS.returnRate.max}
            step={INPUT_LIMITS.returnRate.step}
            suffix="%"
            {...sliderProps}
          />
        </ControlPanel>

        <ResultsPanel $hidden={mobileTab === "input"}>
          <SummaryCards
            adjustedTarget={result.adjustedTarget}
            initialLumpsum={result.initialLumpsum}
            targetPeriod={targetPeriod}
          />

          <ViewToggle activeView={activeView} onChange={setActiveView} />

          {activeView === "graph" ? (
            <DonutChartSection
              chartKey={chartKey}
              gainPercent={result.gainPercent}
              investedPercent={result.investedPercent}
              totalGain={result.totalGain}
              totalInvested={result.totalInvested}
            />
          ) : (
            <YearlyTable yearlyData={result.returnData} />
          )}
        </ResultsPanel>
      </CalculatorWrapper>
    </CalculatorSection>
  );
};

export default GoalPlanningLumpsumCalculator;
